/**
 * AdminHome -- discoverability layer over every admin surface (P42.1).
 *
 * Route: /admin
 *
 * Mirrors the P32/P33 module-home shape: ModuleRubric + h1 +
 * positioning copy + signals strip + 3 INTENT-grouped TileSection
 * blocks (Observability / Ceremony / Catalogue).
 *
 * Admin-only. Non-admins landing here directly see the same
 * "admin only" panel pattern as AuditEventsView / ConnectorsView /
 * SettingsAdminView -- TopNav already hides the link but URL
 * access stays gated.
 */
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { Link } from "react-router-dom";
import { AdminOnlyPanel } from "shared/admin/AdminOnlyPanel";

import { useAdminSettings } from "shared/api/hooks/useAdminSettings";
import { useAuditEvents } from "shared/api/hooks/useAuditEvents";
import { useConnectors } from "shared/api/hooks/useConnectors";
import { useMe } from "shared/api/useMe";
import { ModuleRubric, Tile, TileSection } from "shared/layout/ModuleHomeTile";
import { needsAttention } from "shared/admin/connectorsHelpers";
import { SetUpThisBank } from "shared/admin/SetUpThisBank";

function isoOneDayAgo(): string {
  return new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
}

function useAdminStats() {
  // Stabilise `since` so the audit-events queryKey doesn't change
  // every render -- a recomputed ISO string would refetch forever.
  const [since] = useState(() => isoOneDayAgo());
  const connectors = useConnectors();
  const auditEvents = useAuditEvents({ since, limit: 1, offset: 0 }, { enabled: true });
  const settings = useAdminSettings();

  const connectorRows = connectors.data?.connectors ?? [];
  // D5 -- "14 connectors, 0 failing" was true and useless: thirteen
  // were disabled and the one that was on had never delivered. Count
  // what an operator is expected to act on.
  // E1 -- "1 need attention" is a number. Which one is the question,
  // and the rows that produced the count are right here.
  const failing = needsAttention(connectorRows);
  const failingConnectors = failing.length;

  return {
    connectorCount: connectorRows.length || null,
    failingConnectors,
    // Named, up to a few: a list of thirteen is a wall, and the point
    // is to answer "which one" for the case a person can act on.
    // D3 -- the NAME was text. The product knows which row it means,
    // so the reader should not have to find it again in the grid.
    failingRows: failing.slice(0, 3).map((r) => ({ id: r.id, label: r.label })),
    auditEvents24h: auditEvents.data?.total ?? null,
    appSettingsCount: settings.data?.app_settings.length ?? null,
    loading: connectors.isLoading || auditEvents.isLoading || settings.isLoading,
  };
}

function AdminSignalsStrip() {
  const { t } = useTranslation();
  const s = useAdminStats();
  return (
    <section
      data-testid="admin-signals-strip"
      data-state={s.loading ? "loading" : "ready"}
      className="rounded-lg border border-slate-200 bg-white px-4 py-3"
    >
      <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1 text-sm">
        {s.loading && (
          <span className="text-slate-500 text-xs">
            {t("screens.adminHome.readingAdminSignals")}
          </span>
        )}
        {!s.loading && (
          <>
            <span data-testid="admin-stat-connectors" className="text-slate-800">
              <strong className="tabular-nums">{s.connectorCount ?? "-"}</strong>{" "}
              {t("screens.adminHome.connectors")}
            </span>
            <span
              data-testid="admin-stat-failing"
              className={s.failingConnectors > 0 ? "text-rose-800" : "text-slate-500"}
            >
              {s.failingConnectors > 0 ? (
                <Link
                  data-testid="admin-stat-failing-link"
                  to="/admin/connectors"
                  className="hover:underline"
                >
                  <strong className="tabular-nums">{s.failingConnectors}</strong>{" "}
                  {t("screens.adminHome.needAttention")}
                </Link>
              ) : (
                <>
                  <strong className="tabular-nums">{s.failingConnectors}</strong>{" "}
                  {t("screens.adminHome.needAttention")}
                </>
              )}
              {s.failingRows.length > 0 && (
                <span data-testid="admin-stat-failing-names" className="text-slate-600">
                  {" ("}
                  {s.failingRows.map((r, i) => (
                    <span key={r.id}>
                      {i > 0 ? ", " : ""}
                      <Link
                        data-testid={`admin-stat-failing-name-${r.id}`}
                        to={`/admin/connectors?connector=${r.id}`}
                        className="text-brand-brassDark hover:underline"
                      >
                        {r.label}
                      </Link>
                    </span>
                  ))}
                  {")"}
                </span>
              )}
            </span>
            <span data-testid="admin-stat-audit" className="text-slate-800">
              <strong className="tabular-nums">{s.auditEvents24h ?? "-"}</strong>{" "}
              {t("screens.adminHome.auditEvents24h")}
            </span>
            <span data-testid="admin-stat-settings" className="text-slate-800">
              <strong className="tabular-nums">{s.appSettingsCount ?? "-"}</strong>{" "}
              {t("screens.adminHome.appSettings")}
            </span>
          </>
        )}
      </div>
    </section>
  );
}

export function AdminHome() {
  const { t } = useTranslation();
  const me = useMe();
  const isAdmin = me.data?.is_admin ?? false;

  if (!isAdmin) {
    return <AdminOnlyPanel surface="Admin home" />;
  }

  return (
    <article data-testid="admin-home" className="max-w-6xl space-y-6">
      <header>
        <ModuleRubric>{t("screens.adminHome.framingAdmin")}</ModuleRubric>
        <h1
          data-testid="admin-home-heading"
          className="mt-1 text-2xl font-semibold text-slate-900"
        >
          {t("screens.adminHome.adminConsole")}
        </h1>
        <p
          data-testid="admin-home-positioning"
          className="mt-1 max-w-3xl text-sm text-slate-700"
        >
          {t("screens.adminHome.crossCuttingToolsForRunningTheBank")}
        </p>
      </header>

      <AdminSignalsStrip />

      <SetUpThisBank />

      <TileSection
        heading={t("screens.adminHome.sections.observability")}
        testid="admin-section-observability"
      >
        <Tile
          to="/admin/edge-delays"
          name={t("screens.adminHome.tiles.edgeDelays.name")}
          desc={t("screens.adminHome.tiles.edgeDelays.desc")}
          testid="admin-tile-edge-delays"
          icon="cascade"
        />
        <Tile
          to="/admin/triggers"
          name={t("screens.adminHome.tiles.triggers.name")}
          /* Round 10 D2: the arrow was an ASCII one in a hard-coded
             English string. Said in words, it needs no glyph and no
             mirroring. */
          desc={t("screens.adminHome.tiles.triggers.desc")}
          testid="admin-tile-triggers"
          icon="telemetry"
        />
        <Tile
          to="/admin/mock-exam"
          name={t("screens.adminHome.tiles.mockExam.name")}
          desc={t("screens.adminHome.tiles.mockExam.desc")}
          testid="admin-tile-mock-exam"
          icon="obligation"
        />
        <Tile
          to="/admin/audit-events"
          name={t("screens.adminHome.tiles.auditEvents.name")}
          desc={t("screens.adminHome.tiles.auditEvents.desc")}
          testid="admin-tile-audit-events"
          icon="hash-chain"
        />
        <Tile
          to="/admin/connectors"
          name={t("screens.adminHome.tiles.connectors.name")}
          desc={t("screens.adminHome.tiles.connectors.desc")}
          testid="admin-tile-connectors"
          icon="telemetry"
        />
        <Tile
          to="/admin/users"
          name={t("screens.adminHome.tiles.users.name")}
          desc={t("screens.adminHome.tiles.users.desc")}
          testid="admin-tile-users"
          icon="dual-signoff"
        />
        <Tile
          to="/admin/settings"
          name={t("screens.adminHome.tiles.settings.name")}
          desc={t("screens.adminHome.tiles.settings.desc")}
          testid="admin-tile-settings"
          icon="console"
        />
        <Tile
          to="/admin/service-accounts"
          name={t("screens.adminHome.tiles.serviceAccounts.name")}
          desc={t("screens.adminHome.tiles.serviceAccounts.desc")}
          testid="admin-tile-service-accounts"
          icon="verifier"
        />
        <Tile
          to="/admin/obligation-engine"
          name={t("screens.adminHome.tiles.obligationEngine.name")}
          desc={t("screens.adminHome.tiles.obligationEngine.desc")}
          testid="admin-tile-obligation-engine"
          icon="engine"
        />
        <Tile
          to="/admin/twin-derive"
          name={t("screens.adminHome.tiles.twinDerive.name")}
          desc={t("screens.adminHome.tiles.twinDerive.desc")}
          testid="admin-tile-twin-derive"
          icon="twin"
        />
        <Tile
          to="/admin/engine-runs"
          name={t("screens.adminHome.tiles.engineRuns.name")}
          desc={t("screens.adminHome.tiles.engineRuns.desc")}
          testid="admin-tile-engine-runs"
          icon="engine"
        />
        <Tile
          to="/admin/engine-activity"
          name={t("screens.adminHome.tiles.engineActivity.name")}
          desc={t("screens.adminHome.tiles.engineActivity.desc")}
          testid="admin-tile-engine-activity"
          icon="engine"
        />
        <Tile
          to="/admin/twin-calibration"
          name={t("screens.adminHome.tiles.twinCalibration.name")}
          desc={t("screens.adminHome.tiles.twinCalibration.desc")}
          testid="admin-tile-twin-calibration"
          icon="drill"
        />
      </TileSection>

      <TileSection
        heading={t("screens.adminHome.sections.ceremony")}
        testid="admin-section-ceremony"
      >
        <Tile
          to="/admin/bank-setup"
          name={t("screens.adminHome.tiles.bankSetup.name")}
          desc={t("screens.adminHome.tiles.bankSetup.desc")}
          testid="admin-tile-bank-setup"
          icon="foundation"
        />
        <Tile
          to="/admin/license"
          name={t("screens.adminHome.tiles.license.name")}
          desc={t("screens.adminHome.tiles.license.desc")}
          testid="admin-tile-license"
          icon="verifier"
        />
        <Tile
          to="/admin/examiner-grants"
          name={t("screens.adminHome.tiles.examinerGrants.name")}
          desc={t("screens.adminHome.tiles.examinerGrants.desc")}
          testid="admin-tile-examiner-grants"
          icon="dual-signoff"
        />
        <Tile
          to="/foundation/mvb"
          name={t("screens.adminHome.tiles.mvb.name")}
          desc={t("screens.adminHome.tiles.mvb.desc")}
          testid="admin-tile-mvb"
          icon="mvb"
        />
        <Tile
          to="/board"
          name={t("screens.adminHome.tiles.board.name")}
          desc={t("screens.adminHome.tiles.board.desc")}
          testid="admin-tile-board"
          icon="dossier"
        />
        <Tile
          to="/"
          name={t("screens.adminHome.tiles.dossier.name")}
          desc={t("screens.adminHome.tiles.dossier.desc")}
          testid="admin-tile-dossier"
          icon="dossier"
        />
      </TileSection>

      <TileSection
        heading={t("screens.adminHome.sections.catalogue")}
        testid="admin-section-catalogue"
      >
        <Tile
          to="/admin/data-dictionary"
          name={t("screens.adminHome.tiles.dataDictionary.name")}
          desc={t("screens.adminHome.tiles.dataDictionary.desc")}
          testid="admin-tile-data-dictionary"
          icon="obligation"
        />
        <Tile
          to="/admin/data-import"
          name={t("screens.adminHome.tiles.dataImport.name")}
          desc={t("screens.adminHome.tiles.dataImport.desc")}
          testid="admin-tile-data-import"
          icon="document-locker"
        />
        <Tile
          to="/admin/data-baseline"
          name={t("screens.adminHome.tiles.dataBaseline.name")}
          desc={t("screens.adminHome.tiles.dataBaseline.desc")}
          testid="admin-tile-data-baseline"
          icon="obligation"
        />
        <Tile
          to="/admin/framework-import"
          name={t("screens.adminHome.tiles.frameworkImport.name")}
          desc={t("screens.adminHome.tiles.frameworkImport.desc")}
          testid="admin-tile-framework-import"
          icon="framework"
        />
        <Tile
          to="/admin/documents"
          name={t("screens.adminHome.tiles.documents.name")}
          desc={t("screens.adminHome.tiles.documents.desc")}
          testid="admin-tile-documents"
          icon="document-locker"
        />
        <Tile
          to="/foundation/admin/ibses"
          name={t("screens.adminHome.tiles.ibses.name")}
          desc={t("screens.adminHome.tiles.ibses.desc")}
          testid="admin-tile-ibses"
          icon="ibs"
        />
        <Tile
          to="/foundation/admin/services"
          name={t("screens.adminHome.tiles.services.name")}
          desc={t("screens.adminHome.tiles.services.desc")}
          testid="admin-tile-services"
          icon="constellation"
        />
        <Tile
          to="/foundation/admin/capabilities"
          name={t("screens.adminHome.tiles.capabilities.name")}
          desc={t("screens.adminHome.tiles.capabilities.desc")}
          testid="admin-tile-capabilities"
          icon="capability-map"
        />
        <Tile
          to="/foundation/admin/vendors"
          name={t("screens.adminHome.tiles.vendors.name")}
          desc={t("screens.adminHome.tiles.vendors.desc")}
          testid="admin-tile-vendors"
          icon="blast-radius"
        />
        <Tile
          to="/foundation/admin/products"
          name={t("screens.adminHome.tiles.products.name")}
          desc={t("screens.adminHome.tiles.products.desc")}
          testid="admin-tile-products"
          icon="benchmark"
        />
        <Tile
          to="/foundation/admin/configuration-items"
          name={t("screens.adminHome.tiles.cis.name")}
          desc={t("screens.adminHome.tiles.cis.desc")}
          testid="admin-tile-cis"
          icon="telemetry"
        />
        <Tile
          to="/foundation/admin/channels"
          name={t("screens.adminHome.tiles.channels.name")}
          desc={t("screens.adminHome.tiles.channels.desc")}
          testid="admin-tile-channels"
          icon="capability-map"
        />
        <Tile
          to="/foundation/admin/journeys"
          name={t("screens.adminHome.tiles.journeys.name")}
          desc={t("screens.adminHome.tiles.journeys.desc")}
          testid="admin-tile-journeys"
          icon="journey"
        />
        {/* Round 13 D9 -- P371 modelled both, the MVB recovery sequence
            has been bounded by their capacity ever since, and neither
            had a register: a CSV import of either ended at "Committed"
            with nowhere to go. */}
        <Tile
          to="/foundation/admin/recovery-teams"
          name={t("screens.adminHome.tiles.recoveryTeams.name")}
          desc={t("screens.adminHome.tiles.recoveryTeams.desc")}
          testid="admin-tile-recovery-teams"
          icon="drill"
        />
        <Tile
          to="/foundation/admin/facilities"
          name={t("screens.adminHome.tiles.facilities.name")}
          desc={t("screens.adminHome.tiles.facilities.desc")}
          testid="admin-tile-facilities"
          icon="ibs"
        />
      </TileSection>
    </article>
  );
}
